import React from "react";
import {
  AutoCompleteComponent,
  AutoCompleteModel,
  ChangeEventArgs,
  FilteringEventArgs,
  SelectEventArgs,
} from "@syncfusion/ej2-react-dropdowns";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base/src";
import { AsyncAutoCompleteFieldProps } from "./AsyncAutoCompleteField";
import { Predicate, Query } from "@syncfusion/ej2-data";
import { inactiveCategory } from "./AsyncAutoCompleteODataV4Adaptor";
import { get } from "lodash";
import { useOData } from "../../../../stores/OData/useOData";
import { resolveODataUrl } from "../types";

export const useAsyncAutoCompleteField = (
  props: AsyncAutoCompleteFieldProps
) => {
  const {
    authRequestHeader,
    setupAsyncAutoCompleteODataSource,
    baseActionCompleteEvent,
    baseActionFailureEvent,
  } = useOData(
    {
      getAuthRequestHeader:
        props.getAuthRequestHeader || (() => Promise.resolve(undefined)),
    },
    () => {
      setInitialized(true);
    }
  );

  const autoCompleteRef = React.useRef<AutoCompleteComponent | null>(null);
  const [ready, setReady] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = React.useState<boolean>(false);
  const [instancePredicates, setInstancePredicates] = React.useState<
    Predicate[] | undefined
  >();
  const [initialized, setInitialized] = React.useState<boolean>(false);
  const [localValue, setLocalValue] = React.useState<any>(props.value);
  const [localSearchValue, setLocalSearchValue] = React.useState<any>(
    props.searchValue
  );
  const [localData, setLocalData] = React.useState<any>();
  const [localAllData, setLocalAllData] = React.useState<any>();
  const [autoCompleteSettings, setAutoCompleteSettings] = React.useState<
    AutoCompleteModel & DefaultHtmlAttributes
  >();

  const [queryKey, setQueryKey] = React.useState<string>();

  const localAutofillDataRef = React.useRef<any>(props.autoFillData);

  const handleValueChange = (newValue: string) => {
    if (initialized && autoCompleteRef.current) {
      /**
       * Setting local value to detect change faster than rerendering,
       * allowing the UI to adjust styles like require as it occurs.
       */
      setLocalValue(newValue);
      autoCompleteRef.current.value = newValue;
    }
  };

  React.useEffect(() => {
    setQueryKey("initial-key");
  }, [initialized]);

  React.useEffect(() => {
    if (
      autoCompleteRef.current &&
      localSearchValue &&
      !(localAutofillDataRef.current && localAutofillDataRef.current.length > 0)
    ) {
      searchOnPopup(localSearchValue);
    }
  }, [isDataLoaded, localSearchValue]);

  React.useEffect(() => {
    if (
      autoCompleteRef.current &&
      localAutofillDataRef.current &&
      localAutofillDataRef.current.length > 0
    ) {
      autoFillOnPopup();
    }
  }, [isDataLoaded, localAutofillDataRef.current]);

  React.useEffect(() => {
    if (isDataLoaded && initialized && autoCompleteRef.current) {
      handleValueChange(props.value as string);
    }
  }, [props.value, isDataLoaded]);

  React.useEffect(() => {
    if (props.searchValue !== localSearchValue) {
      setLocalSearchValue(props.searchValue);
    }
  }, [props.searchValue]);

  React.useEffect(() => {
    if (initialized) {
      if (props.submitAllAssigments && localValue && localData === undefined) {
        const data = localAllData.find((x: any) => x.value === localValue);
        props.onSubmit && props.onSubmit(data);
      }
    }
  }, [localAllData]);

  /** Observe and Save Filter Settings */
  React.useEffect(() => {
    if (initialized) {
      setup();
    }
  }, [queryKey]);

  React.useEffect(() => {
    const requestKey = JSON.stringify(props.autoCompleteSettings);
    if (requestKey !== queryKey) {
      setQueryKey(requestKey);
    }
  }, [props.autoCompleteSettings]);

  React.useEffect(() => {
    if (initialized) {
      props.onSubmit && props.onSubmit(localData);
    }
  }, [localData]);

  React.useEffect(() => {
    const predicates = props.autoCompleteSettings?.query?.queries
      .filter((x) => x.fn === "onWhere")
      .map((x) => x.e as Predicate);
    setInstancePredicates(predicates);
  }, [props.autoCompleteSettings?.query?.queries]);

  // Events
  const createdHandler = () => {
    if (
      localSearchValue &&
      !(localAutofillDataRef.current && localAutofillDataRef.current.length > 0)
    ) {
      searchOnPopup(localSearchValue);
    }

    if (
      localAutofillDataRef.current &&
      localAutofillDataRef.current.length > 0
    ) {
      autoFillOnPopup();
    }
  };

  const autoFillOnPopup = () => {
    if (
      !!autoCompleteRef.current &&
      !props.disabled &&
      localAutofillDataRef.current &&
      localAutofillDataRef.current.length > 0
    ) {
      autoCompleteRef.current.hidePopup();
      setTimeout(() => {
        if (!!autoCompleteRef.current) {
          autoCompleteRef.current.dataSource = localAutofillDataRef.current;
          (
            autoCompleteRef.current?.element as HTMLInputElement
          ).addEventListener("focus", () => {
            if (
              localAutofillDataRef.current &&
              localAutofillDataRef.current.length > 0
            ) {
              localAutofillDataRef.current = undefined;
              setup(true);
            }
          });

          showSearchPopup();
        }
      }, 100);
    }
  };

  const searchOnPopup = (searchText: string) => {
    if (!!autoCompleteRef.current && !props.disabled && searchText) {
      autoCompleteRef.current.hidePopup();
      (autoCompleteRef.current?.element as HTMLInputElement).value = searchText;

      setTimeout(() => {
        autoCompleteRef.current!.query = buildQuery(searchText);
        (autoCompleteRef.current?.element as HTMLInputElement).value =
          searchText;
        showSearchPopup();
        setLocalSearchValue(undefined);
      }, 100);
    }
  };

  const localValueLessThanMinLength =
    props.autoCompleteSettings?.minLength &&
    (!localValue || localValue.length < props.autoCompleteSettings?.minLength);

  const showSearchPopup = () => {
    if (
      !!autoCompleteRef.current &&
      initialized &&
      !props.disabled &&
      !localValueLessThanMinLength
    ) {
      autoCompleteRef.current.showPopup();
    }
  };

  const handleSearchSelect = async (args: SelectEventArgs) => {
    if (get(args.itemData, "category") === inactiveCategory) {
      args.cancel = true;
      autoCompleteRef.current?.hidePopup();

      setTimeout(() => {
        if (!!autoCompleteRef.current && initialized) {
          autoCompleteRef.current.showPopup();
        }
      }, 100);
    } else {
      if (!!autoCompleteRef.current && initialized) {
        setLocalData(args.itemData);
      }
    }
  };

  const handleChange = async (args: ChangeEventArgs) => {
    if (!!autoCompleteRef.current && initialized && args.isInteracted) {
      if (!args?.value) {
        /** If localData is undefined it never triggers the update, in which case we trigger in this clause */
        if (!!localData) {
          setLocalData(undefined);
        } else {
          props.onSubmit && props.onSubmit(undefined);
        }
      } else {
        setLocalData(args.itemData);
      }
    }
  };

  const buildDefaultQuery = () => {
    return new Query()
      .select(props.fieldNames || ["label", "value"])
      .sortBy(props.sortBy || "label")
      .take(50);
  };

  const buildQuery = (searchText: string): Query => {
    let query = buildDefaultQuery();

    const containsPredicate = new Predicate(
      autoCompleteRef.current!.fields.text || "label",
      "contains",
      searchText,
      true
    );

    if (!autoCompleteRef.current!.query) {
      query = query.where(containsPredicate);
    } else {
      let predicate = containsPredicate;
      if (props.searchBy) {
        predicate = props.searchBy(searchText);
      }

      if (predicate) {
        if (instancePredicates) {
          instancePredicates.forEach((instancePredicate) =>
            query.where(predicate.and(instancePredicate))
          );
        } else {
          query.where(predicate);
        }
      }
    }
    return query;
  };

  const handleFiltering = (args: FilteringEventArgs) => {
    if (autoCompleteRef.current) {
      args.preventDefaultAction = true;

      let query = buildQuery(args.text);
      args.updateData(autoCompleteRef.current.dataSource, query);
    }
  };

  const defaultAutoCompleteSettings: AutoCompleteModel & DefaultHtmlAttributes =
    {
      autofill: true,
      showClearButton: true,
      highlight: false,
      allowCustom: false,
      fields: { text: "label", value: "value" },
      itemTemplate: "<table><tr><td>${label}</td></tr><table>",
      /** Inmutable */
      created: createdHandler,
      select: handleSearchSelect,
      focus: showSearchPopup,
      onClick: showSearchPopup,
      change: handleChange,
      filtering: handleFiltering,
      actionFailure: baseActionFailureEvent,
      actionComplete: (args: Object) => {
        const data = get(args, "result", []);
        setLocalAllData(data);
        baseActionCompleteEvent();
      },
    };

  const setup = async (force?: boolean) => {
    if (!force && !queryKey) {
      return;
    }
    if (force || !!authRequestHeader) {
      // Setup Properties
      const settings = {
        ...defaultAutoCompleteSettings,
        ...(props.autoCompleteSettings || {}),
      } as AutoCompleteModel & DefaultHtmlAttributes;

      /** Set Item Template Wrapper */
      settings.itemTemplate =
        "<span class='ucl-async-autocomplete-item ucl-async-autocomplete-item-disabled-${isDeleted}'>" +
        String(settings.itemTemplate) +
        "</span>";

      /** System not allows groupby if not in list option is set up */
      if (
        settings.fields &&
        (!!props.hasNotInListOption || !!props.groupByActiveStatus)
      ) {
        settings.fields.groupBy = "category";
      }

      const resolvedODataUrl = resolveODataUrl(props);

      // Set Datasource
      if (
        localAutofillDataRef.current &&
        localAutofillDataRef.current.length > 0
      ) {
        settings.dataSource = localAutofillDataRef.current;
      } else {
        const dm = setupAsyncAutoCompleteODataSource(
          resolvedODataUrl,
          settings.fields,
          props.hasNotInListOption,
          props.groupByActiveStatus
        );
        settings.dataSource = dm;
      }

      settings.value = props.value;
      setAutoCompleteSettings(settings);
      setReady(true);
    }
  };

  return {
    autoCompleteRef,
    ready,
    isLoading,
    setIsLoading,
    isDataLoaded,
    setIsDataLoaded,
    localValue,
    setLocalValue,
    autoCompleteSettings,
    handleValueChange,
    buildDefaultQuery,
    localAutofillData: localAutofillDataRef.current,
  };
};
